import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Save, User as UserIcon, Calendar as CalendarIcon, MapPin, HeartHandshake } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Manual correction panel for personal data captured (perhaps incorrectly)
 * by the voice recognition during the Vidente Virtual session.
 *
 * Each field stands on its own — the user only fills the ones that need to
 * be corrected, then Submit pushes the deltas to the backend.
 *
 * Also supports adding a third person to be "channeled" by Cristopher.
 */
export function VidenteDataCorrectionPanel({ open, onClose, sessionId, token, initial, onSaved }) {
  const init = initial || {};
  const [form, setForm] = useState({
    name: init.name || "",
    birth_date: init.birth_date || "",
    age: init.age || "",
    city: init.city || "",
    country: init.country || "",
    motive: init.motive || "",
    third_party_name: init.third_party_name || "",
    third_party_relation: init.third_party_relation || "",
    third_party_birth_date: init.third_party_birth_date || "",
    third_party_age: init.third_party_age || "",
    third_party_notes: init.third_party_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [showThirdParty, setShowThirdParty] = useState(
    !!(init.third_party_name || init.third_party_relation || init.third_party_birth_date)
  );

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    // Only send fields that have changed from initial (or are non-empty if no init)
    const delta = {};
    Object.keys(form).forEach((k) => {
      const newVal = (form[k] || "").trim();
      const oldVal = (init[k] || "").trim();
      if (newVal && newVal !== oldVal) delta[k] = newVal;
    });
    if (Object.keys(delta).length === 0) {
      toast.info("No hay cambios para enviar");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/vidente/session/${sessionId}/correct-data`,
        delta,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Datos actualizados. Cristopher ya los tomó en cuenta.");
      onSaved?.(data.personal_data || {});
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="correct-data-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 20 }}
            className="relative w-full max-w-lg bg-gradient-to-b from-[#0d0a14] to-[#0a0a0a] border border-[#d4af37]/30 rounded-2xl shadow-2xl shadow-[#d4af37]/10 p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="correct-data-panel"
          >
            <button
              data-testid="correct-data-close"
              onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-neutral-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>

            <h2 className="font-serif text-xl text-white mb-1">Corregir datos personales</h2>
            <p className="text-xs text-neutral-400 mb-5 leading-snug">
              Si Cristopher pronunció algo diferente a lo que dijiste, escríbelo aquí. Solo llena los campos
              que están mal — los demás déjalos en blanco.
            </p>

            <div className="space-y-3">
              <Row icon={<UserIcon className="w-3.5 h-3.5 text-[#d4af37]" />} label="Nombre completo correcto">
                <input
                  data-testid="cd-name"
                  type="text"
                  value={form.name}
                  onChange={setField("name")}
                  placeholder="Ej: Juan Antonio Pérez"
                  className="w-full bg-black/50 border border-white/10 focus:border-[#d4af37]/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                />
              </Row>

              <div className="grid grid-cols-2 gap-3">
                <Row icon={<CalendarIcon className="w-3.5 h-3.5 text-[#d4af37]" />} label="Fecha de nacimiento">
                  <input
                    data-testid="cd-birth"
                    type="date"
                    value={form.birth_date}
                    onChange={setField("birth_date")}
                    className="w-full bg-black/50 border border-white/10 focus:border-[#d4af37]/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                  />
                </Row>
                <Row label="Edad (si no recuerdas la fecha)">
                  <input
                    data-testid="cd-age"
                    type="text"
                    inputMode="numeric"
                    value={form.age}
                    onChange={setField("age")}
                    placeholder="Ej: 34"
                    className="w-full bg-black/50 border border-white/10 focus:border-[#d4af37]/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                  />
                </Row>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Row icon={<MapPin className="w-3.5 h-3.5 text-[#d4af37]" />} label="Ciudad">
                  <input
                    data-testid="cd-city"
                    type="text"
                    value={form.city}
                    onChange={setField("city")}
                    placeholder="Ej: Hermosillo"
                    className="w-full bg-black/50 border border-white/10 focus:border-[#d4af37]/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                  />
                </Row>
                <Row label="País">
                  <input
                    data-testid="cd-country"
                    type="text"
                    value={form.country}
                    onChange={setField("country")}
                    placeholder="Ej: México"
                    className="w-full bg-black/50 border border-white/10 focus:border-[#d4af37]/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                  />
                </Row>
              </div>

              <Row label="Motivo real de tu consulta">
                <textarea
                  data-testid="cd-motive"
                  rows={2}
                  value={form.motive}
                  onChange={setField("motive")}
                  placeholder="Ej: dudas sobre mi pareja, problemas de dinero..."
                  className="w-full bg-black/50 border border-white/10 focus:border-[#d4af37]/50 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none"
                />
              </Row>
            </div>

            {/* Third person section */}
            <div className="mt-6 pt-5 border-t border-white/10">
              <button
                data-testid="cd-toggle-third"
                onClick={() => setShowThirdParty((v) => !v)}
                className="w-full text-left flex items-center justify-between gap-2 text-sm text-purple-300 hover:text-purple-200"
              >
                <span className="flex items-center gap-2">
                  <HeartHandshake className="w-4 h-4" />
                  ¿Quieres canalizar la energía de otra persona?
                </span>
                <span className="text-xs">{showThirdParty ? "Ocultar" : "Mostrar"}</span>
              </button>

              {showThirdParty && (
                <div className="mt-3 space-y-3">
                  <p className="text-[11px] text-neutral-500 leading-snug">
                    Para que las cartas hablen claro de esa persona, comparte lo que sepas.
                    Cristopher trabaja con lo que tengas — no necesitas saberlo todo.
                  </p>
                  <Row label="Nombre de la persona">
                    <input
                      data-testid="cd-tp-name"
                      type="text"
                      value={form.third_party_name}
                      onChange={setField("third_party_name")}
                      placeholder="Ej: María"
                      className="w-full bg-black/50 border border-white/10 focus:border-purple-400/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                    />
                  </Row>
                  <Row label="Relación contigo">
                    <input
                      data-testid="cd-tp-rel"
                      type="text"
                      value={form.third_party_relation}
                      onChange={setField("third_party_relation")}
                      placeholder="Ej: ex pareja, jefe, amiga..."
                      className="w-full bg-black/50 border border-white/10 focus:border-purple-400/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                    />
                  </Row>
                  <div className="grid grid-cols-2 gap-3">
                    <Row label="Fecha de nacimiento (si la sabes)">
                      <input
                        data-testid="cd-tp-birth"
                        type="date"
                        value={form.third_party_birth_date}
                        onChange={setField("third_party_birth_date")}
                        className="w-full bg-black/50 border border-white/10 focus:border-purple-400/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                      />
                    </Row>
                    <Row label="Edad aproximada">
                      <input
                        data-testid="cd-tp-age"
                        type="text"
                        inputMode="numeric"
                        value={form.third_party_age}
                        onChange={setField("third_party_age")}
                        placeholder="Ej: 32"
                        className="w-full bg-black/50 border border-white/10 focus:border-purple-400/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                      />
                    </Row>
                  </div>
                  <Row label="Notas (ciudad, profesión, rasgo emocional, lo que sepas)">
                    <textarea
                      data-testid="cd-tp-notes"
                      rows={2}
                      value={form.third_party_notes}
                      onChange={setField("third_party_notes")}
                      placeholder="Ej: vive en CDMX, trabaja en marketing, es muy callada..."
                      className="w-full bg-black/50 border border-white/10 focus:border-purple-400/50 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none"
                    />
                  </Row>
                </div>
              )}
            </div>

            <button
              data-testid="cd-submit"
              onClick={handleSave}
              disabled={saving}
              className="mt-6 w-full btn-gold py-3 rounded-full uppercase text-xs tracking-widest font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Enviar correcciones"}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Row({ icon, label, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-400 mb-1">
        {icon}
        {label}
      </label>
      {children}
    </div>
  );
}
